import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { PropertyContactInfo } from './PropertyContactInfo';
import type { OfficeHourEntry } from './PropertyContactInfo';
import { ctaArrowCss, ctaHoverCss } from '../utils/ctaHover';

const PREFIX = 'contactus';

// Shown only when neither a call-tracking number nor a property phone exists.
const PHONE_PLACEHOLDER = '(555) 555-5555';

const addressSlotDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text:    { label: 'Address Text', type: 'text',  defaultField: 'property_full_address' },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (PropertyContactInfo). Editor overrides still win.
    url:     { label: 'Map URL',      type: 'url' },
    _target: { label: 'Target',       type: 'text',  defaultValue: '_blank' },
  },
};

export function ContactUs() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const formSlot = useBlockSlot(`${PREFIX}.form_block`, { defaultValue: 'multihub-lead-form' });

  // Contact info tokens — lifted here so the editor sidebar surfaces them
  // under the ContactUs section. PropertyContactInfo is purely presentational.
  const visitLabel = useContentToken(`${PREFIX}.visit_label`);
  const callLabel  = useContentToken(`${PREFIX}.call_label`);
  const hoursLabel = useContentToken(`${PREFIX}.hours_label`);
  const addressSlot = useSlotGroup(`${PREFIX}.address`, addressSlotDef);
  const phoneToken  = useContentToken(`${PREFIX}.phone`);
  const fallbackPhone = usePropertyField('property_phone') as string | null;
  // Resolution chain: call-tracking number (token defaultField/override) →
  // property_phone → placeholder. The token's defaultValue is empty so a missing
  // call-tracking number falls through to property_phone instead of the placeholder.
  const phone = phoneToken || fallbackPhone || PHONE_PLACEHOLDER;

  const officeHours = useCollectionBinding<OfficeHourEntry>(`${PREFIX}.office_hours`, {
    collection: 'office_hours',
  });

  return (
    <section ref={sectionRef as React.RefObject<HTMLElement>} className="reveal-fade-up cu-section">
      <div className="cu-container">
        <div className="cu-grid">
          <div className="cu-info-col">
            <PropertyContactInfo
              prefix={PREFIX}
              visitLabel={visitLabel}
              callLabel={callLabel}
              hoursLabel={hoursLabel}
              address={addressSlot.text}
              addressUrl={addressSlot.url}
              addressTarget={addressSlot._target}
              phone={phone}
              officeHours={officeHours}
              addressGroupPath={`${PREFIX}.address`}
              addressGroupDef={addressSlotDef}
            />
          </div>

          <div className="cu-form-col">
            <EditableToken path="contactus.form_block" as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : null}
            </EditableToken>
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — literal paths so the linter can find each token.
          PropertyContactInfo uses dynamic `${prefix}.*` paths on its EditableTokens,
          so these hidden elements satisfy token-missing-editable-wrapper. */}
      <EditableToken path="contactus.visit_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactus.call_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactus.phone"       as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactus.hours_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      {/* Slot-group anchor — satisfies slot-group-missing-editable-wrapper for the dynamic prefix path in PropertyContactInfo */}
      <div style={{ display: 'none' }}>
        <EditableSlotGroup groupPath="contactus.address" definition={addressSlotDef}>
          <span />
        </EditableSlotGroup>
      </div>

      <style>{`
        .cu-section {
          padding: 2.5rem 0 3.5rem;
          background: var(--vibe-color-background);
        }
        @media (min-width: 768px) {
          .cu-section { padding: 3rem 0 4rem; }
        }
        .cu-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .cu-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .cu-container { padding: 0 2rem; } }

        .cu-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 1024px) {
          .cu-grid {
            grid-template-columns: 5fr 7fr;
            gap: 2.5rem;
            align-items: start;
          }
        }

        .cu-form-col { position: relative; }

        /* Pill CTAs — override multihub-lead-form inline border-radius */
        .cu-form-col .mh-lead-form-submit-button,
        .cu-form-col .mh-lead-form-success-cta {
          border-radius: 9999px !important;
        }
        ${ctaHoverCss([
          '.cu-form-col .mh-lead-form-submit-button',
          '.cu-form-col .mh-lead-form-success-cta',
        ])}
        ${ctaArrowCss([
          '.cu-form-col .mh-lead-form-submit-button',
          '.cu-form-col .mh-lead-form-success-cta',
        ])}
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactUs',
  displayName: 'Contact Us',
  tokens: {
    'contactus.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactus.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'contactus.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'contactus.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
    'contactus.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'contactus.address': addressSlotDef,
  },
  collectionBindings: {
    'contactus.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactUs as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactUs;
